import { useEffect, useRef, useState } from "react";
import { SHORTCUTS, shortcutLabel } from "../hooks/use-shortcuts";
import { Icon } from "./Icon";
import { RunOptions } from "./RunOptions";

const baseName = (path: string) => path.slice(path.lastIndexOf("/") + 1);

interface EditorActionsProps {
  /** What ▷ runs: the pinned entry point, else the open file. */
  target: string;
  /** The entry point is pinned ("Always run …"). */
  pinned: boolean;
  /** The open file. */
  openFile: string;
  /** The open file is a test file (▷ runs its tests). */
  openIsTest: boolean;
  /** Why the target can't run (then there's no Run button, just this), if it can't. */
  cannotRun: string | undefined;
  /** How the open file's runner finds tests, if it has one. */
  tests: string | undefined;
  onRun: () => void;
  onTest: () => void;
  onPin: (path: string | undefined) => void;
  /** The open file's runner, if it has one. */
  runner: { id: string; name: string } | undefined;
}

/**
 * The editor's Run control, like VS Code's: ▷ runs the entry point (the pinned file, else the
 * open one) or, in a test file, its tests; ⌄ has the rest. Says plainly when nothing can run.
 */
export function EditorActions(props: EditorActionsProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [menuOpen]);

  if (props.cannotRun) {
    const [first] = props.cannotRun.split(". ");
    return (
      <div className="editor-actions">
        <span className="run-unavailable" title={props.cannotRun} data-testid="run-unavailable">
          <Icon name="play" size={12} />
          {first?.replace(" in the browser", " here")}
        </span>
      </div>
    );
  }

  const testsFirst = props.openIsTest && !props.pinned;
  const runLabel = `Run ${baseName(props.target)}`;
  const choose = (action: () => void) => () => {
    setMenuOpen(false);
    action();
  };
  return (
    <div className="editor-actions">
      <div className="run-split" ref={menuRef}>
        <button
          type="button"
          className="run-main"
          aria-label={testsFirst ? "Run tests" : "Run"}
          title={
            testsFirst
              ? `Run the tests in ${baseName(props.openFile)} (${shortcutLabel(SHORTCUTS.runTests)})`
              : `${runLabel}${props.pinned ? " (always run)" : ""} (${shortcutLabel(SHORTCUTS.run)})`
          }
          onClick={testsFirst ? props.onTest : props.onRun}
        >
          <Icon name="play" size={13} />
          <span className="run-main-label">{testsFirst ? "Run tests" : runLabel}</span>
          {props.pinned && <Icon name="pin" size={11} />}
        </button>
        <button
          type="button"
          className="run-more"
          aria-label="More run actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <Icon name="chevron" size={12} />
        </button>
        {menuOpen && (
          <div className="menu run-menu" role="menu" aria-label="Run">
            <button type="button" role="menuitem" onClick={choose(props.onRun)}>
              <span>{runLabel}</span>
              <kbd>{shortcutLabel(SHORTCUTS.run)}</kbd>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={choose(props.onTest)}
              title={props.tests}
              aria-disabled={!props.tests || undefined}
            >
              <span>Run tests{props.openIsTest ? ` in ${baseName(props.openFile)}` : ""}</span>
              <kbd>{shortcutLabel(SHORTCUTS.runTests)}</kbd>
            </button>
            <hr className="menu-separator" />
            {props.pinned ? (
              <button type="button" role="menuitem" onClick={choose(() => props.onPin(undefined))}>
                <span>Run the open file instead</span>
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                onClick={choose(() => props.onPin(props.openFile))}
                title="Run always runs this file, whichever file is open"
              >
                <span>Always run {baseName(props.openFile)}</span>
              </button>
            )}
          </div>
        )}
      </div>
      <RunOptions runner={props.runner} />
    </div>
  );
}
